<template>
  <div id="main-layout" :class="appClasses" :aria-hidden="needToHide ? true : false"
    :style="needToHide ? 'display: none;' : ''" :tabindex="needToHide ? -1 : null">
    <continue-last :needBlur="needBlurBackground" v-if="lastLearnedCondition"
      :name="`${user.lastName} ${user.firstName}`" :lastLearnedInfo="thematicsByLevel.lastLearnedInfo" />
    <audio-player v-if="hasToken && audioSources" :sources="audioSource" :autoplay="false" :preload="true" :loop="false"
      :xhrWithCredentials="true" />
    <android-smart-app-banner v-if="showSmartAppBanner" />
    <ios-smart-app-banner v-if="showSmartAppBanner" />
    <system-authorization-push-modal v-if="hasToken && isShowSystemAuthorization"
      @closeSystemAuthorization="closeSystemAuthorization" />
    <nav-bar v-if="isLogin" />
    <div v-if="!isPageNotFound" :class="[
      { modalBlur: needBlurBackground },
      'container-fluid',
      'layout-wrapper',
      'p-0',
      'm-0',
    ]">
      <div class="row no-gutters layout-inner-wrapper">
        <transition enter-active-class="animated slideInLeft faster">
          <div id="tab-bar-container" v-if="routeOptions && routeOptions.sidebar" :class="[
            'col-auto',
            'nav-bar',
            { island: $route.name === 'Island' },
          ]">
            <div class="tab-bar" style="min-width: 72px">
              <tab-bar />
            </div>
          </div>
        </transition>
        <div class="col view" :class="{
          'has-navbar': routeOptions && routeOptions.sidebar,
        }">
          <div ref="screenWindow" id="router-container" :class="this.screenClass" :style="detectThematicBackgroundColor
            ? `background-color: ${detectThematicBackgroundColor};`
            : ''
            ">
            <transition mode="out-in" :name="transitionName" enter-active-class="animated fadeIn faster"
              leave-active-class="animated fadeOut faster">
              <router-view v-if="!isLoading" ref="router">
                <template slot="page-header" slot-scope="{
                    slotTitle,
                    slotClose,
                    slotBreadcrumb,
                    slotProgress,
                    slotSettings,
                    slotSearch,
                    slotSearchText,
                    slotInfo,
                    slotSearchProgress,
                  }">
                  <transition appear mode="in-out" name="slide" enter-active-class="animated slideInDown faster"
                    leave-active-class="animated slideOutUp faster">
                    <div class="row no-gutters app-page-header-wrapper" style="min-height: 62px">
                      <div class="col-12">
                        <page-header :title="slotTitle" :close="slotClose" :breadcrumb="slotBreadcrumb"
                          :progress="slotProgress" :settings="slotSettings" :search="slotSearch" :info="slotInfo"
                          :searchInProgress="slotSearchProgress" :searchText="slotSearchText" />
                      </div>
                    </div>
                  </transition>
                </template>
              </router-view>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <page-not-found v-if="isPageNotFound" />
  </div>
</template>
<script>
/* eslint-disable object-shorthand */
import { mapGetters, mapActions } from 'vuex';
import UserAction from '@/store/models/user/user_action';
import { fromShortToLong } from '@/components/shared/languageCodes';
import PushNotificationManager from '@/push_notification_manager';
import ThematicColorMixin from '@/components/shared/elements/mixins/ThematicColorMixin';
import DetectIncognito from '@/detect_incognito';
import GenericModal from '@/components/shared/elements/modal/GenericModal';
import NavBar from '@/components/modules/landing/components/NavBar';
import i18n from '@/lang/lang';
import ModalMixin from '@/components/shared/elements/mixins/ModalMixin';
import DKTMixin from '@/components/shared/elements/mixins/DKTMixin';
import AuthenticatorMixin from '@/components/shared/elements/mixins/AuthneticatorMixin';
import BasicMixin from '@/components/shared/elements/mixins/BasicMixin';
import cookieHandler from '@/cookie_handler';
import ValidMobileDeviceMixin from '@/components/shared/elements/mixins/ValidMobileDeviceMixin';
import { bootstrap } from 'vue-gtag';

const InPlacePopover = () =>
  import('@/components/shared/elements/modal/InPlacePopover');

const SuccessModal = () =>
  import('@/components/shared/elements/modal/SuccessModal');
const RewardModal = () =>
  import('@/components/modules/profile/components/RewardModal');
const IslandExtraOfferModal = () =>
  import('@/components/shared/elements/modal/IslandExtraOfferModal');
const PushRequestPermissionModal = () =>
  import('@/components/shared/elements/modal/PushRequestPermissionModal');

const SystemAuthorizationPushModal = () =>
  import('@/components/shared/elements/modal/SystemAuthorizationPushModal');
const PushNotificationSettingsModal = () =>
  import('@/components/shared/elements/modal/PushNotificationSettingsModal');
const AndroidSmartAppBanner = () =>
  import('@/components/shared/AndroidSmartAppBanner');

const IosSmartAppBanner = () => import('@/components/shared/IosSmartAppBanner');

const TabBar = () => import('@/components/modules/tabbar/TabBar');

const LevelUpModal = () =>
  import('@/components/shared/elements/modal/LevelUpModal');
const PageHeader = () =>
  import('@/components/shared/elements/bands/PageHeader');
const Breadcrumb = () =>
  import('@/components/shared/elements/bands/Breadcrumb');
const Audio = () => import('@/components/shared/elements/player/Audio');
const HostedFields = () =>
  import('@/components/modules/shop/components/HostedFields');

const PageNotFound = () => import('@/components/shared/PageNotFound');

const ContinueLastLearnedCard = () =>
  import('@/components/modules/island/components/ContinueLastLearnedCard');

export default {
  name: 'MainLayout',
  mixins: [
    ThematicColorMixin,
    ModalMixin,
    DKTMixin,
    BasicMixin,
    ValidMobileDeviceMixin,
    AuthenticatorMixin,
  ],
  components: {
    NavBar,
    TabBar,
    PageHeader,
    InPlacePopover,
    breadcrumb: Breadcrumb,
    SystemAuthorizationPushModal,
    AndroidSmartAppBanner,
    IosSmartAppBanner,
    PageNotFound,
    'audio-player': Audio,
    'continue-last': ContinueLastLearnedCard,
  },
  props: {
    needBlurBackground: {
      type: Boolean,
    },
    needToHide: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      presisUsageDataInterval: undefined,
      sessionLeftTimeout: undefined,
      translateModalWasShown: false,
      startPlaying: false,
      audioSources: null,
      isBlur: false,
      tooltipComponent: null,
      transitionName: 'fade',
      announcement: null,
      isShowSystemAuthorization: false,
      appUsageStartTime: new Date(),
      lastTitleValue: '',
      changedLang: false,
      isFirefoxPrivate: false,
    };
  },
  async created() {
    this.isLoading = true;
    this.readAccessibilitySettings();
    this.checkTokens();
    this.watchGoogleTranslator();
    this.setNeedLastLearnedCard(true);
    // this.setUserLanguage();
    this.subscribeToPushMessages();
    // If user already logged in
    if (!this.user.isKozigMember) {
      window.$cookies.set('logged-in', false, null, null, '.xeropan.com');
      window.$cookies.remove('logged-in');
    } else {
      window.$cookies.set('logged-in', true, null, null, '.xeropan.com');
    }
    if ((window.$cookies.get('logged-out') == true || window.$cookies.get('logged-out') == 'true') && !location.href.includes('authentication') && this.user.isKozigMember) {
      await this.userLogout(true);
      window.$cookies.set('logged-out', false, null, null, '.xeropan.com');
      window.$cookies.remove('logged-out');
    }
    if ((window.$cookies.get('dkt-logged-out') == true || window.$cookies.get('dkt-logged-out') == 'true') && !location.href.includes('ifm') && this.user.isDktMember && !this.user.isNeptunMember) {
      this.setSessionTimeoutModalStatus(false);
      await this.userLogout(true);
      document.cookie =
        'dkt-logged-out=;expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; HostOnly; domain=.xeropan.com; path=/';
    }
    if (
      this.hasToken &&
      this.getAuthToken &&
      this.getAuthToken !== 'undefined'
    ) {
      this.saveUserProfile();
      this.checkAndSetMobileSupportModalStatus();
      this.setIsNexusUserToStore();
      //   if (this.user.isClassroomMember) {
      //     this.getOpenAssignments();
      //   }

      //   this.getUserSettings().then((res) => {
      //     if (
      //       (this.readUserSettings('finishedOnboarding') ||
      //         this.user.registrationStatus === 'registered') &&
      //       this.$route.name == 'LandingScreen'
      //     ) {
      //       window.location.href = '/';
      //     }
      //   });
      setTimeout(() => {
        if (this.hasToken) {
          this.persistUsageData(true);
        }
      }, 1000);
      this.presisUsageDataInterval = setInterval(this.persistUsageData, 60000);
      //   this.storeUserAction({
      //     action_type: UserAction.userActionTypes.userActionAppOpen,
      //     value: 1,
      //   });
    } else {
      this.isLoading = false;
    }
    //  else if (
    //   this.$route.meta.group === 'Island' &&
    //   this.$route.meta.group !== 'Classroom'
    // ) {
    //   this.$router.push('/');
    // }

    // if (this.isLessonWithoutUser) {
    //   this.$store.commit(
    //     'settings/SET_USER_SELECTED_LANGUAGE',
    //     this.$route.query.lang,
    //     { root: true },
    //   );
    // }
    document.addEventListener('visibilitychange', this.setUpDocumentTitle);
    this.$eventBus.onPlayExpression(this, (payload) => {
      this.audioSources = payload.url;
      this.startPlaying = true;
    });
    this.$eventBus.onTooltip(this, (payload) => {
      this.tooltipComponent = payload;
    });
    this.$eventBus.onTabBarButtonClicked(this, () => {
      // TODO: Do something with global tab bar button click event
    });
    this.$eventBus.onTabBarTabClicked(this, (payload) => {
      this.goPage(payload.link);
    });
    this.$eventBus.onTabBarMainButtonClicked(this, () => {
      if (this.$route.name !== 'Island') {
        if (this.readUserSettings('firstTimeThematic') === true) {
          this.$router.push({
            name: 'Island',
            force: true,
          });
          location.href = '/';
          this.setUserSettings({ id: 'firstTimeThematic', value: false });
        } else {
          this.$router.push({
            name: 'Island',
          });
        }
      }
    });
    this.$eventBus.onLevelUp(this, () => {
      this.showLevelUpModal();
    });
    this.$eventBus.onClickedProPopup(this, (payload) => {
      this.clickedProPopup(payload);
    });
    this.$eventBus.onShowIslandNotification(this, (payload) => {
      this.showIslandNotification(payload);
    });
    this.$eventBus.onAnnouncementTriggered(this, (payload) => {
      this.announcement = payload;
      if (this.announcement.isExpirable) {
        this.announcementTimeout();
      }
    });
    this.$eventBus.onAnnouncementExpired(this, () => {
      this.announcement = null;
      window.clearInterval(this.announcementIntervalId);
    });
    this.$eventBus.onContentNotAllowed(this, (putToSales = false) => {
      if (putToSales) {
        this.$router.push('/pro/');
      } else {
        this.$router.push('/');
      }
      this.$eventBus.notificationTriggered({
        title: this.$t('app.errors.contentNotAllowed.title'),
        description: this.$t('app.errors.contentNotAllowed.description'),
        imgPath: '/static/img/icons/not_available_content.svg',
        backgroundColor: 'white',
        timeout: 3500,
      });
    });
    this.$eventBus.onSessionLeft(this, (sessionTimeLeft) => {
      if (!this.getIsKHUser || location.href.includes('zero-password')) {
        if (sessionTimeLeft == 0) {
          this.sessionLogoutMethod(false);
        } else {
          this.sessionLeftEventMethod(sessionTimeLeft);
        }
      }
    });
    this.$eventBus.onClearPresisUsageDataInterval(this, () => {
      this.clearPresisUsageDataInterval();
    });
    this.$eventBus.onSwitchIsland(this, (payload) => {
      this.setIsland(payload);
    });
  },
  async mounted() {
    this.setDevAndAdvData();
    // this.disableTrackingAppsForKretaUser();
    await this.bootstrapGoogleAdsAndCookieConsent();
    this.setAppTitle();
    if (process.env.NODE_ENV === 'production' && (this.loggedInUserProfile && !this.isKretaUser)) {
      this.insertPixelAds();
    }
    this.initYbug();
  },
  updated() {
    if (
      document.title == '' ||
      document.title == 'app.title' ||
      document.title == null
    ) {
      this.setUpDocumentTitle();
    }
  },
  beforeDestroy() {
    this.$eventBus.offPlayExpression();
    this.$eventBus.offStartButtonModal();
    this.$eventBus.offTooltip();
    this.$eventBus.offTabBarButtonClicked();
    this.$eventBus.offTabBarMainButtonClicked();
    this.$eventBus.offLevelUp();
    this.$eventBus.offClickedProPopup();
    this.$eventBus.offShowIslandNotification();
    this.$eventBus.offAnnouncementTriggered();
    this.$eventBus.offAnnouncementExpired();
    this.$eventBus.offContentNotAllowed();
    this.$eventBus.offSessionLeft();
    this.$eventBus.offClearPresisUsageDataInterval();
    this.$eventBus.offSwitchIsland();
    document.removeEventListener('visibilitychange', this.setUpDocumentTitle);
    this.removeGoogleTranslatorListener();
  },
  watch: {
    $route(to, from) {
      if (this.$refs?.screenWindow) {
        setTimeout(() => {
          this.$refs.screenWindow.scrollTop = 0;
        }, 340);
      }
      if (to.name !== 'Island') {
        this.setNeedLastLearnedCard(false);
      }

      this.setLastRoute({
        name: from.name,
        query: from.query,
      });
    },
    getSiteLanguage: {
      handler() {
        setTimeout(() => {
          this.changedLang = true;
          this.lastTitleValue = this.$t('app.title');
          this.setUpDocumentTitle();
        }, 500);
      },
    },
    getWCAGSettings: {
      deep: true,
      handler(val, oldval) {
        if (val?.optionallyBiggerCursor != oldval?.optionallyBiggerCursor) {
          this.setOptionallyBiggerCursor(val.optionallyBiggerCursor);
        }
      },
    },
    // isLessonWithoutUser(newValue, oldValue) {
    //   if (newValue) {
    //     this.checkAndSetForceWeb();
    //   }
    // },
  },
  computed: {
    ...mapGetters({
      hasToken: 'hasToken',
      getAuthToken: 'getAuthToken',
      loggedInUserProfile: 'users/loggedInUserProfile',
      islandProgression: 'contents/islandProgression',
      translation: 'contents/translation',
      showSalesPopup: 'sales/showSalesPopup',
      userState: 'users/userState',
      getSiteLanguage: 'settings/getSiteLanguage',
      readUserSettings: 'settings/getUserSettings',
      isPageNotFound: 'settings/isPageNotFound',
      deviceToken: 'device/deviceToken',
      thematicsByLevel: 'contents/thematicsByLevel',
      needLastLearnedCard: 'settings/needLastLearnedCard',
      getIsNexusUser: 'settings/getIsNexusUser',
      isKretaUser: 'users/isKretaUser',
      isNeptunUser: 'users/isNeptunUser',
      getMobileSupportModalStatus: 'settings/getMobileSupportModalStatus',
      getApiToken: 'users/getApiToken',
      getWCAGSettings: 'settings/getWCAGSettings',
      getLastRoute: 'settings/getLastRoute',
      getDktOptions: 'settings/getDktOptions',
      getIsKHUser: 'settings/getIsKHUser',
    }),
    devName() {
      return (
        this.deviceName !== undefined &&
        this.deviceName !== '' &&
        this.deviceName !== null
      );
    },
    devToken() {
      return (
        this.deviceToken !== undefined &&
        this.deviceToken !== '' &&
        this.deviceToken !== null
      );
    },
    advToken() {
      return (
        this.advertisingToken !== undefined &&
        this.advertisingToken !== '' &&
        this.advertisingToken !== null
      );
    },
    isPushSupported() {
      return (
        (navigator.appVersion.includes('Chrome') &&
          !navigator.appVersion.includes('OPR')) ||
        navigator.userAgent.toLowerCase().indexOf('firefox') > -1
      );
    },
    showSmartAppBanner() {
      return (
        (!location.pathname.split('/')[1].includes('dkt') &&
          !location.href.includes('kozigazgatas') &&
          !location.href.includes('authentication') &&
          !location.href.includes('instituteId') &&
          !this.getIsKHUser && !this.isNeptunUser && !location.href.includes('zero-password'))
          && this.validDevice()
      );
    },
    userProfileScopes() {
      if (!this.hasToken) {
        return [];
      }
      const scopes = [
        this.$constants.USER_SCOPES.USER,
        this.$constants.USER_SCOPES.RESULTS,
        this.$constants.USER_SCOPES.STATISTICS,
        this.$constants.USER_SCOPES.LANGUAGE_PROGRESS,
      ];
      if (!this.isKretaUser) {
        scopes.push(this.$constants.USER_SCOPES.LEAGUE);
        scopes.push(this.$constants.USER_SCOPES.FRIENDS);
      }
      return scopes;
    },
    isMobile() {
      return window.innerWidth < 768;
    },
    isLogin() {
      return false;
      // return (
      //   this.$route.name === 'AuthenticationScreen' ||
      //   this.$route.name === 'OnboardingScreen'
      // );
    },
    isLessonWithoutUser() {
      return (
        this.$route.query['x-key'] !== undefined &&
        this.$route.query['x-key'] !== null
      );
    },
    lastLearnedCondition() {
      return (
        this.thematicsByLevel.lastLearnedInfo &&
        this.needLastLearnedCard &&
        this.$route.name === 'Island'
      );
    },
    appClasses() {
      if (this.getSiteLanguage == 'ar_AE') {
        return 'rtl-support';
      }
      return '';
    },
    user() {
      return this.loggedInUserProfile.user;
    },
    routeOptions() {
      return this.$route.meta.options;
    },
    audioSource() {
      return [this.audioSources];
    },
    screenClass() {
      if (this.$route.name === 'Island') {
        return 'p-0 m-0 row screen no-gutters';
      } else if (this.$route.meta.group === 'LandingScreen') {
        return 'row no-gutters landing';
      } else if (this.$route.name == 'Library') {
        return 'row screen no-gutters library';
      } else if (this.$route.name === 'OnboardingScreen') {
        return 'row screen is-onboarding no-gutters';
      }
      return 'row screen no-gutters';
    },
    promotion() {
      return this.userState.promotion;
    },
  },
  methods: {
    ...mapActions({
      setInvalidationStatus: 'settings/setInvalidationStatus',
      getUserProfile: 'users/getUserProfile',
      storeUserAction: 'users/storeUserAction',
      userLogout: 'users/logout',
      logoutBackend: 'users/logoutBackend',
      keepUserSignedIn: 'users/keepUserSignedIn',
      getUserSettings: 'settings/getUserSettingsFromServer',
      storeWebPushInfo: 'users/storeWebPushInfo',
      changedPushNotificationStatus: 'users/changedPushNotificationStatus',
      setUserSettings: 'settings/setUserSettings',
      getOpenAssignments: 'classroom/getOpenAssignments',
      setNeedLastLearnedCard: 'settings/setNeedLastLearnedCard',
      setIsNexusUser: 'settings/setIsNexusUser',
      setLastRoute: 'settings/setLastRoute',
      getCurrentlyLoadedIsland: 'contents/getCurrentlyLoadedIsland',
      setSessionTimeoutModalStatus: 'persisted/setSessionTimeoutModalStatus',
      setMobileSupportModalStatus: 'settings/setMobileSupportModalStatus',
      setWCAGSetting: 'settings/setWCAGSetting',
      setIsClassLanguageCollison: 'contents/setIsClassLanguageCollison',
    }),
    initYbug() {
      const ybug = document.createElement('script');
      ybug.innerHTML = `
        (function() {
          window.ybug_settings = {
            'id': '2swkxjr1gh5dn3rvk72w',
            'launcher_position': 'right-middle',
            language_override: document.documentElement.lang,
            nps: true,
            email_required: true,
            translate: {
              'feedbackForm.NpsLabel': ${JSON.stringify(this.$t('ybug.feedback.title'))},
              'feedbackForm.Nps.Unlikely': ${JSON.stringify(this.$t('ybug.feedback.dontLike'))},
              'feedbackForm.Nps.Likely': ${JSON.stringify(this.$t('ybug.feedback.like'))},
            },
            oncancel: function() {
              if (location.href.includes('/lessons/') || location.href.includes('/conversations/')) {
                document.getElementById('modal').style.display = 'block';
                document.getElementById('popper').classList = 'modalBlur';
              }
            },
            onaftersend: function() {
              if (location.href.includes('/lessons/') || location.href.includes('/conversations/')) {
                document.getElementById('modal').style.display = 'block';
                document.getElementById('popper').classList = 'modalBlur';
              }
            }
          };
          var ybug = document.createElement('script');
          ybug.type = 'text/javascript';
          ybug.async = true;
          ybug.src = 'https://widget.ybug.io/button/' + window.ybug_settings.id + '.js';
          var s = document.getElementsByTagName('script')[0];
          s.parentNode.insertBefore(ybug, s);
        })();
      `;
      document.head.appendChild(ybug);
    },
    setOptionallyBiggerCursor(val) {
      if (val) {
        document.body.classList.add('bigger-cursor');
      } else {
        document.body.classList.remove('bigger-cursor');
      }
    },
    readAccessibilitySettings() {
      const contrastMode =
        cookieHandler.getCookie('vision-impaired') === 'true';
      const biggerCursor = cookieHandler.getCookie('bigger-cursor') === 'true';

      const object = {};
      if (contrastMode != undefined) object.contrastMode = contrastMode;
      if (biggerCursor != undefined) {
        object.optionallyBiggerCursor = biggerCursor;
      }
      this.setOptionallyBiggerCursor(biggerCursor);
      this.setWCAGSetting(object);
    },
    setDevAndAdvData() {
      if (!this.devName || !this.devToken || !this.advToken) {
        const userAgent = navigator.userAgent.substring(0, 230);
        const date = this.$moment().format('YYYY-MM-DD HH:mm');
        this.setDeviceName(`${userAgent} ${date}`);
        this.setDeviceToken(this.$uuid());
        this.setAdvertisingToken(this.$uuid());
      }
    },
    setAppTitle() {
      setTimeout(() => {
        if (document.visibilityState == 'hidden') {
          document.title = this.$t('app.hiddenTabTitle');
        } else {
          document.title = this.$t('app.title');
        }
      }, 900);
    },
    setIsNexusUserToStore() {
      const isNexusMember =
        this.loggedInUserProfile.user.isNexusMember || this.getIsNexusUser;
      this.setIsNexusUser(isNexusMember);
    },
    async bootstrapGoogleAdsAndCookieConsent() {
      const isDktScreen = location.pathname.split('/')[1].includes('dkt');

      if (
        // !process.env.IS_STAGING &&
        !this.isKretaUser &&
        !this.getIsKHUser &&
        !isDktScreen &&
        window.innerWidth > 768
      ) {
        if (!window.CookieFirst) {
          this.loadCookieBot();
        }

        await bootstrap(); // This will start google tracking
      } else if (this.isKretaUser) {
        const cookieFirstInCookie = cookieHandler.getCookie(
          'cookiefirst-consent',
        );
        if (cookieFirstInCookie) {
          document.cookie =
            'cookiefirst-consent=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }
      }
    },
    saveUserProfile() {
      this.setIsClassLanguageCollison(true);
      this.getUserProfile({ scopes: this.userProfileScopes })
        .then(() => {
          this.isLoading = false;
          if (process.env.NODE_ENV == 'production' && !this.isKretaUser) {
            require('../../../public/static/seo/facebookPixel');
          }
          this.setIsClassLanguageCollison(false);
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    checkAndSetMobileSupportModalStatus() {
      if (
        (this.getMobileSupportModalStatus || this.isLessonWithoutUser) &&
        this.isMobile
      ) {
        this.setMobileSupportModalStatus(true);
      } else {
        this.setMobileSupportModalStatus(false);
      }
    },
    subscribeToPushMessages() {
      if (this.isPushSupported && navigator.serviceWorker != undefined) {
        navigator.serviceWorker.addEventListener('message', async (event) => {
          const result = await this.changedPushNotificationStatus({
            notificationId: event.data.id,
            status: 'opened',
          });
          if (event.data.type != undefined) {
            switch (event.data.type) {
              case 1:
              case 146: {
                this.$router.push('/skills/');
                break;
              }
              case 144: {
                this.$router.push('/pro/');
                break;
              }
              default: {
                if (this.$router.currentRoute.name == 'Island') {
                  location.reload();
                } else {
                  this.$router.push('/');
                }
                break;
              }
            }
          }
        });
      }
    },
    setUserLanguage() {
      if (this.isLessonWithoutUser) {
        this.$store.commit(
          'settings/SET_USER_SELECTED_LANGUAGE',
          this.$route.query.lang,
          { root: true },
        );
      } else if (this.$route.query.locale || this.$route.query.lang) {
        this.$store.commit(
          'settings/SET_USER_SELECTED_LANGUAGE',
          fromShortToLong[this.$route.query.locale],
          { root: true },
        );
      } else if (!this.hasToken) {
        const full = window.location.host;
        const parts = full.split('.');
        const sub = parts[0];
        if (sub && (sub.length == 2 || sub.length == 3)) {
          i18n.locale = fromShortToLong[sub];
          const html = document.documentElement;
          const englishSites = ['zh', 'uz', 'ky'];
          html.setAttribute('lang', englishSites.includes(sub) ? 'en' : sub);
        } else {
          const findLanguage = this.getSiteLanguage;
          i18n.locale = findLanguage;
          const html = document.documentElement;
          const englishSites = ['zh', 'uz', 'ky'];
          const foundLanguage = findLanguage.split('_')[0];
          html.setAttribute('lang', englishSites.includes(foundLanguage) ? 'en' : foundLanguage);
        }
      } else {
        this.$store.commit(
          'settings/SET_USER_SELECTED_LANGUAGE',
          this.getSiteLanguage,
          { root: true },
        );
      }
    },
    saveIsland(id, level) {
      this.getCurrentlyLoadedIsland({ island: id, level });
    },
    getIslandLevel(value) {
      switch (value) {
        case 'island_0':
          this.saveIsland('island_0', 0);
          return 0;
        case 'island_1':
          this.saveIsland('island_1', 1);
          return 1;
        case 'island_2':
          this.saveIsland('island_2', 2);
          return 2;
        case 'island_3':
          this.saveIsland('island_3', 3);
          return 3;
        case 'island_4':
          this.saveIsland('island_4', 4);
          return 4;
        case 'island_5':
          this.saveIsland('island_5', 5);
          return 5;
        case 'island_6':
          this.saveIsland('island_6', 6);
          return 6;
        case 'island_7':
          this.saveIsland('island_7', 7);
          return 7;
        case 'island_8':
          this.saveIsland('island_8', 8);
          return 8;
        case 'island_9':
          this.saveIsland('island_9', 9);
          return 9;
        case 'island_10':
          this.saveIsland('island_10', 10);
          return 10;
        case 'island_11':
          this.saveIsland('island_11', 11);
          return 11;
        case 'island_12':
          this.saveIsland('island_12', 12);
          return 12;
        default:
          this.saveIsland('island_1', 1);
          return 0;
      }
    },
    setIsland(payload) {
      this.getIslandLevel(payload.level);
      if (payload.needLastLearned) {
        this.setNeedLastLearnedCard(true);
      }
    },
    checkTokens() {
      const tokenInStore = this.loggedInUserProfile.user.token;
      const tokenInCookie = this.getAuthToken;
      // if (this.isKretaUser && tokenInCookie && tokenInStore) {
      //   tokenInStore = this.loggedInUserProfile.apiToken;
      //   tokenInCookie = this.getApiToken;
      // }
      if (tokenInStore != tokenInCookie && tokenInStore && tokenInCookie) {
        // this.userLogout(true);
        this.getUserProfile();
      }
    },
    clearPresisUsageDataInterval() {
      if (this.presisUsageDataInterval) {
        clearInterval(this.presisUsageDataInterval);
        this.presisUsageDataInterval = undefined;
      }
    },
    insertPixelAds() {
      const pixelAdsScript = document.createElement('script');
      pixelAdsScript.innerHTML =
        "!function(f,b,e,v,n,t,s) {if(f.fbq)return;n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)}; if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0'; n.queue=[];t=b.createElement(e);t.async=!0; t.src=v;s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s)}(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js'); fbq('init', '200500380113638'); fbq('track', 'PageView')";
      document.head.appendChild(pixelAdsScript);
    },
    sessionLeftEventMethod(sessionTime) {
      if (this.sessionLeftTimeout) {
        clearTimeout(this.sessionLeftTimeout);
        this.sessionLeftTimeout = undefined;
      }
      const showFirstModalAt = 60 * 1000;
      const sessionTimeLeft = sessionTime * 1000 - showFirstModalAt - 5 * 1000;
      this.sessionLeftTimeout = setTimeout(() => {
        if (this.isKretaUser) {
          this.$eventBus.showModal({
            bind: {
              is: GenericModal,
              hasExit: false,
              titleText: this.$t('app.dkt.seassionTimeOut.firstPopup.title'),
              description: this.$t(
                'app.dkt.seassionTimeOut.firstPopup.desciption',
              ),
              time: showFirstModalAt,
              descriptionEnd: this.$t(
                'app.dkt.seassionTimeOut.firstPopup.desciptionEnd',
              ),
              okText: this.$t(
                'app.dkt.seassionTimeOut.firstPopup.action.keepSigned',
              ),
              okAction: () => {
                this.keepUserSignedIn();
                this.$eventBus.closeModal();
              },
              cancelText: this.$t(
                'app.dkt.seassionTimeOut.firstPopup.action.logout',
              ),
              cancelAction: () => {
                this.sessionLogoutMethod();
              },
              escAction: () => {
                this.keepUserSignedIn();
                this.$eventBus.closeModal();
              },
            },
            on: {
              timedOut: () => {
                this.sessionLogoutMethod();
              },
            },
          });
        }
      }, sessionTimeLeft);
    },
    async sessionLogoutMethod(needLogoutBackend = true) {
      if (!location.href.includes('zero-password')) {
        if (this.sessionLeftTimeout) {
          clearTimeout(this.sessionLeftTimeout);
          this.sessionLeftTimeout = undefined;
        }
        this.$eventBus.closePopper();
        this.$eventBus.closeModal();
        if (needLogoutBackend) {
          this.logoutBackend();
        }
        // Set cookie
        cookieHandler.setCookie('session_timeout', true);
        window.$cookies.set('dkt-logged-out', true, null, null, '.xeropan.com');
        PushNotificationManager.unsubscribeUser(() => { });
        await this.userLogout(true);
        location.href = '/login';
        this.sessionLeftEventLogoutMethod();
      }
    },
    sessionLeftEventLogoutMethod() {
      this.setSessionTimeoutModalStatus(true);
    },
    watchGoogleTranslator() {
      document.addEventListener(
        'DOMSubtreeModified',
        this.googleTranslatorHandler,
        true,
      );
    },
    removeGoogleTranslatorListener() {
      document.removeEventListener(
        'DOMSubtreeModified',
        this.googleTranslatorHandler,
      );
    },
    googleTranslatorHandler(e) {
      setTimeout(() => {
        const htmlClassList = [
          ...document.getElementsByTagName('html')[0].classList,
        ];
        const pageIsTranslated =
          htmlClassList.includes('translated-ltr') ||
          htmlClassList.includes('translated-rtl');
        if (!pageIsTranslated) {
          this.translateModalWasShown = false;
        }
        if (
          e.target.tagName === 'HTML' &&
          window.google &&
          e.target.className.match('translated')
        ) {
          this.translateModalWasShown = true;
          this.showTranslatorModal();
        } else if (pageIsTranslated && this.translateModalWasShown == false) {
          this.translateModalWasShown = true;
          this.showTranslatorModal();
        }
      }, 500);
    },
    showTranslatorModal() {
      this.$eventBus.showModal({
        bind: {
          is: GenericModal,
          hasExit: false,
          titleText: this.$t('app.listeners.googleTranslate.title'),
          description: this.$t('app.listeners.googleTranslate.description'),
          okText: this.$t('app.listeners.googleTranslate.okText'),
          okAction: () => {
            this.$eventBus.closeModal();
          },
        },
      });
    },
    loadCookieBot() {
      const keys = {
        default: '03607313-b105-4220-b652-cf7a6bfdb47a',
        ar: '6ea06f9a-0e66-45bd-9836-7be36aad177e',
        de: '4cdf96d2-a8c9-4488-8043-90408400b688',
        en: '054552cf-866e-4e61-9425-dee2ea64b594',
        es: 'c7b3097d-2c68-45e8-bb71-16df7ba3c73c',
        fr: '997a8d97-0761-4587-97fc-8410ad98ea11',
        hi: '5ca1a18d-0824-454e-84ab-226fa188d12a',
        hu: 'a2ead39f-cf46-4f11-b791-671d2b0fd51e',
        id: '02861040-7962-49f9-b3ae-d565c4b23fdd',
        it: '96fd14fb-4f03-41d1-b8cd-ae1b5f63a543',
        ja: '54474463-1354-4f22-979c-561e2aa03a36',
        ko: 'bbc66adf-0d0f-45c5-bab8-7ca351090978',
        pl: '061bc2c1-77fa-4f92-b6c1-70c8b8fca901',
        pt: 'ea9baf72-b9e6-4552-95b3-b1ea0499cf8f',
        ro: 'bbd86a75-8813-48bb-a2d7-409baf796389',
        ru: 'a6841bc0-22a2-47af-af76-69bea0fe2954',
        tr: '943d0fec-8164-415c-9171-c73eb5984153',
        vi: 'e3ac9ab8-5ead-4369-8207-8b0d96a0453c',
      };
      const domain = window.location.host;
      const language = domain.substring(0, 2);
      const key = keys[language] == null ? keys.default : keys[language];
      const cookieBotScript = document.createElement('script');
      cookieBotScript.setAttribute(
        'src',
        `https://consent.cookiefirst.com/sites/${domain}-${key}/consent.js`,
      );
      document.head.appendChild(cookieBotScript);
    },
    setUpDocumentTitle() {
      if (document.visibilityState == 'hidden') {
        if (this.changedLang) {
          this.changedLang = false;
        } else {
          this.lastTitleValue = document.title;
        }
        document.title = this.$t('app.hiddenTabTitle');
      } else {
        document.title =
          this.lastTitleValue == '' || 'app.title'
            ? this.$t('app.title')
            : this.lastTitleValue;
      }
    },
    goPage(page) {
      if (page === '/' && this.readUserSettings('firstTimeThematic') === true) {
        if (this.$route.path != page) {
          this.$router.push({ path: page, force: true });
        }
        location.href = '/';
        this.setUserSettings({ id: 'firstTimeThematic', value: false });
      } else if (this.$route.path != page) {
        this.$router.push(page);
      }
    },
    closeSystemAuthorization() {
      this.isShowSystemAuthorization = false;
    },
    continueToDailyRemainder() {
      this.isShowSystemAuthorization = false;
      this.$eventBus.showModal({
        bind: {
          is: PushNotificationSettingsModal,
          closeable: true,
          cancelAction: () => {
            this.$eventBus.closeModal();
          },
        },
      });
    },
    clickedProPopup(payload) {
      const classroomProModal = this.promotion.getSalesPopup(
        payload.type,
        this.okActionProPopup,
        this.cancelActionProPopup,
        payload.teacherPicture,
      );
      const basicModal = this.promotion.getSalesPopup(
        payload.type,
        this.okActionProPopup,
        this.cancelActionProPopup,
      );
      const classroomPro = payload.type === 'classroomProContentClicked';
      const currentModal = classroomPro ? classroomProModal : basicModal;
      this.storeUserAction({
        action_type: UserAction.userActionTypes.userActionOpenProFunctionPopup,
        value: 1,
      }).then((response) => {
        this.$eventBus.showModal({
          bind: currentModal,
          noScrollChange: payload.noScrollChange,
          backgroundColor: 'transparent',
          isScaledUp: true,
        });
      });
    },
    showIslandNotification(payload) {
      this.getUserProfile().then(() => {
        if (payload) {
          if ([138].includes(payload.notificationType)) {
            // full screen streak, rank, manual
            this.showIslandAutoAndManualSalesNotification(payload);
          } else if ([139].includes(payload.notificationType)) {
            // standard grace
            this.showIslandGraceSalesNotification(payload);
          } else if ([140].includes(payload.notificationType)) {
            // broken crow resub
            this.showIslandResubSalesNotification(payload);
          } else if ([137].includes(payload.notificationType)) {
            this.$eventBus.showModal({
              bind: {
                is: RewardModal,
                text: payload.message,
                okText: payload.okText,
                cancelText: payload.cancelText,
              },
            });
          } else if ([136].includes(payload.notificationType)) {
            DetectIncognito.isPrivateMode().then((isPrivateMode) => {
              if (!isPrivateMode) {
                if (
                  this.isPushSupported &&
                  navigator.serviceWorker != undefined
                ) {
                  this.$eventBus.showModal({
                    bind: {
                      is: PushRequestPermissionModal,
                      closeable: true,
                      okAction: () => {
                        this.$eventBus.closeModal();
                        if (
                          'Notification' in window &&
                          Notification.permission != 'granted'
                        ) {
                          this.isShowSystemAuthorization = true;
                        }
                        PushNotificationManager.checkSubscriptionStatus(
                          (response) => {
                            // serviceWorker registration check
                            if (
                              response.isSubscribed != undefined &&
                              response.isSubscribed
                            ) {
                              // unsubscribe serviceWorker
                              PushNotificationManager.unsubscribeUser(
                                (response) => {
                                  this.serviceWorkerRegister();
                                },
                              );
                            } else {
                              this.serviceWorkerRegister();
                            }
                          },
                        );
                      },
                      cancelAction: () => {
                        this.$eventBus.closeModal();
                      },
                    },
                  });
                }
              }
            });
          } else if ([147].includes(payload.notificationType)) {
            // island extra offer
            this.showIslandExtraOfferNotification(payload);
          }
        }
      });
    },
    showIslandExtraOfferNotification(islandNotificationResponse) {
      this.$eventBus.showModal({
        bind: {
          is: IslandExtraOfferModal,
          type: 'islandExtraOffer',
          cancelText: this.$t(
            'app.islandNotification.gracePeriodCancelActionLabel',
          ),
          cancelAction: this.cancelActionProPopup,
        },
      });
    },
    showIslandAutoAndManualSalesNotification(islandNotificationResponse) {
      this.$eventBus.showModal({
        bind: this.promotion.getSalesPopup(
          'autoAndManualSales',
          this.okActionProPopup,
          this.cancelActionProPopup,
        ),
        backgroundColor: 'transparent',
        isScaledUp: true,
      });
    },
    serviceWorkerRegister() {
      PushNotificationManager.startRegistration((response) => {
        if (response.isAccept) {
          const data = {
            device_token: this.deviceToken,
            endpoint: response.endpoint,
            auth: response.auth,
            p256dh: response.p256dh,
          };
          this.storeWebPushInfo(data).then((response) => {
            this.continueToDailyRemainder();
          });
        } else {
          this.isShowSystemAuthorization = false;
        }
      });
    },
    showIslandGraceSalesNotification(islandNotificationResponse) {
      // gtag measurement point
      if (this.$gtag) {
        this.$gtag.event('Update needed', {
          event_category: 'renew PRO',
          event_label: 'Popup',
        });
      }

      // facebook pixel measurement point
      if (typeof fbq !== 'undefined') {
        fbq('trackCustom', 'Update needed', {
          eventCategory: 'renew PRO',
          eventLabel: 'Popup',
        });
      }

      this.$eventBus.showModal({
        bind: {
          is: SuccessModal,
          titleText: this.$t('app.islandNotification.gracePeriodTitle'),
          descriptionText: this.$t(
            'app.islandNotification.gracePeriodDescription',
          ),
          okText: this.$t('app.islandNotification.gracePeriodOkActionLabel'),
          okAction: this.okActionGrace,
          cancelText: this.$t(
            'app.islandNotification.gracePeriodCancelActionLabel',
          ),
          cancelAction: this.cancelActionProPopup,
        },
      });
    },
    showIslandResubSalesNotification(islandNotificationResponse) {
      this.$eventBus.showModal({
        bind: this.promotion.getSalesPopup(
          'resubSales',
          this.okActionProPopup,
          this.cancelActionProPopup,
        ),
        overflow: 'visible',
        backgroundColor: 'transparent',
      });
    },
    okActionProPopup(product) {
      this.$eventBus.closeModal();
      this.$eventBus.closePopper();
      if (product) {
        this.$eventBus.showPopper({
          bind: {
            is: HostedFields,
            product,
          },
        });
      } else {
        this.goPage({ name: 'ShopScreen' });
      }
    },
    okActionGrace() {
      this.$eventBus.closeModal();
      this.goPage({ name: 'Cards' });
    },
    cancelActionProPopup() {
      this.$eventBus.closeModal();
    },
    showLevelUpModal() {
      this.isBlur = !this.isBlur;
      this.$eventBus.$emit('ShowModal', {
        bind: {
          closeable: true,
          is: LevelUpModal,
        },
        width: '312px',
      });
    },
    deleteAnnouncement() {
      this.$eventBus.$emit('AnnouncementExpired');
    },
    announcementTimeout() {
      this.announcementIntervalId = setTimeout(() => {
        this.deleteAnnouncement();
      }, 5000);
    },
    persistUsageData(isCreated = false) {
      this.setInvalidationStatus(false);
      const endDate = new Date();
      const seconds =
        (endDate.getTime() - this.appUsageStartTime.getTime()) / 1000;
      let userActionType = UserAction.userActionTypes.userActionTimeInapp;
      const parsedValue = Number.parseInt(seconds, 10);
      let userActionValue = parsedValue > 200 ? 199 : parsedValue;
      if (isCreated) {
        userActionType = UserAction.userActionTypes.userActionAppOpen;
        userActionValue = 1;
      }
      this.storeUserAction({
        action_type: userActionType,
        value: userActionValue,
      });
      this.appUsageStartTime = new Date();
    },
  },
};
</script>

<style lang="scss">
/*
 * Custom styles
 */
@import "@/assets/css/global.scss";

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.tooltip.text-option-tooltip {
  .tooltip-inner {
    padding-right: 18px;
    padding-left: 18px;
    font-size: 14px;
    background-color: #48b0f7;
    margin-bottom: 10px;
    // box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  }

  .tooltip-arrow {
    display: none;
  }
}

:root [data-theme="contrast"] {
  .tooltip.text-option-tooltip {
    .tooltip-inner {
      background-color: #07578d;
    }
  }
}

.tooltip.profile-class-item,
.tooltip.lesson-cell-tooltip {
  .tooltip-inner {
    padding-right: 18px;
    padding-left: 18px;
    font-size: 14px;
    background-color: #48b0f7;
    // box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  }
}

:root [data-theme="contrast"] {

  .tooltip.profile-class-item,
  .tooltip.lesson-cell-tooltip {
    .tooltip-inner {
      background-color: #07578d;
    }
  }
}

.tooltip.lesson-cell-tooltip {
  .tooltip-arrow {
    display: none;
  }
}

#main-layout {
  position: relative;
  height: 100%;

  .layout-wrapper {
    height: 100%;

    .layout-inner-wrapper {
      height: 100%;

      .col-tab {
        flex: 0 0 64px !important;
      }

      .view {
        transition: all 0.5s ease-in-out;
        position: relative;
        overflow: hidden;
        padding: 0;
        height: 100%;
      }

      .screen {
        position: relative;
        background-color: var(--lightWhite);
        overflow-y: auto;
        height: 100%;

        &.is-onboarding {
          @media (min-width: $medium-screen) {
            overflow-y: hidden;
          }
        }

        &:after {
          margin: 0 0 64px 0;
        }

        >div {
          transition: all 0.5s ease-in-out;
        }
      }

      .landing {
        height: 100%;
        overflow: auto;
      }
    }
  }
}

.col-auto.island {
  .tab-bar {
    position: relative;
    z-index: 2;
    box-shadow: 0 3px 6px 0 rgba(var(--shadowBlue), 0.2);

    #tab-bar {
      /deep/ .tab-bar-container {
        box-shadow: none !important;
      }
    }
  }
}

@media (max-width: $medium-screen) {
  .view.has-navbar {
    margin-bottom: 58px !important;

    .screen {
      height: calc(100% - 58px) !important;
    }
  }

  .nav-bar {
    width: 0px !important;
    z-index: z("side-bar");
  }
}
</style>
